import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { initials } from "@/lib/formatters";
import { updateContact } from "@/api/contacts";
import type { Company, Contact } from "@/api/types";
import { HIDDEN_DETAIL_HINT, isHidden } from "@/components/shared/HiddenDetail";

const schema = z.object({
  full_name: z.string().min(1, "Name is required"),
  title: z.string().nullable(),
  email: z
    .union([z.literal(""), z.string().email("Invalid email")])
    .nullable(),
  phone: z.string().nullable(),
  linkedin_url: z
    .union([z.literal(""), z.string().url("Invalid URL")])
    .nullable(),
  is_decision_maker: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

interface ContactEditDialogProps {
  contact: Contact | null;
  company: Company | null;
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (updated: Contact) => void;
}

export function ContactEditDialog({
  contact,
  company,
  projectSlug,
  open,
  onOpenChange,
  onSaved,
}: ContactEditDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: "",
      title: null,
      email: null,
      phone: null,
      linkedin_url: null,
      is_decision_maker: false,
    },
  });

  useEffect(() => {
    if (open && contact) {
      reset({
        full_name: contact.full_name,
        title: contact.title,
        email: contact.email,
        phone: contact.phone,
        linkedin_url: contact.linkedin_url,
        is_decision_maker: contact.is_decision_maker,
      });
    }
  }, [open, contact, reset]);

  // Withheld outside the reader's call session: shown locked, and never sent back —
  // the form was served null, saving it must not erase the real value.
  const emailHidden = isHidden(contact, "email");
  const phoneHidden = isHidden(contact, "phone");

  async function onSubmit(values: FormValues) {
    if (!contact) return;
    try {
      const updated = await updateContact(projectSlug, contact.id, {
        full_name: values.full_name,
        title: emptyToNull(values.title),
        ...(emailHidden ? {} : { email: emptyToNull(values.email) }),
        ...(phoneHidden ? {} : { phone: emptyToNull(values.phone) }),
        linkedin_url: emptyToNull(values.linkedin_url),
        is_decision_maker: values.is_decision_maker,
      });
      toast.success("Contact saved");
      onSaved(updated);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  const isDM = watch("is_decision_maker");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Edit contact</DialogTitle>
          <DialogDescription className="sr-only">
            Update this contact&rsquo;s details.
          </DialogDescription>
        </DialogHeader>

        {contact && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-5"
            noValidate
          >
            <div className="flex items-center gap-3 rounded-xl bg-slate-50/60 px-3 py-3">
              <Avatar className="h-10 w-10 border border-slate-200">
                <AvatarFallback className="bg-indigo-100 text-sm font-semibold text-indigo-700">
                  {initials(contact.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-slate-900">
                  {contact.full_name}
                </div>
                {company && (
                  <div className="truncate text-xs text-slate-500">
                    {company.name}
                  </div>
                )}
              </div>
            </div>

            <Field
              label="Full name"
              error={errors.full_name?.message}
            >
              <Input
                {...register("full_name")}
                className="h-10 rounded-lg border-slate-200"
                placeholder="Jane Doe"
              />
            </Field>

            <Field label="Title" error={errors.title?.message}>
              <Input
                {...register("title")}
                className="h-10 rounded-lg border-slate-200"
                placeholder="CEO, CFO…"
              />
            </Field>

            <Field label="Email" error={errors.email?.message}>
              <Input
                type="email"
                {...register("email")}
                disabled={emailHidden}
                title={emailHidden ? HIDDEN_DETAIL_HINT : undefined}
                className="h-10 rounded-lg border-slate-200"
                placeholder={emailHidden ? "Email hidden" : "name@company.com"}
              />
            </Field>

            <Field label="Phone" error={errors.phone?.message}>
              <Input
                type="tel"
                {...register("phone")}
                disabled={phoneHidden}
                title={phoneHidden ? HIDDEN_DETAIL_HINT : undefined}
                className="h-10 rounded-lg border-slate-200"
                placeholder={phoneHidden ? "Phone hidden" : "+1 415 555 0100"}
              />
            </Field>

            <Field
              label="LinkedIn"
              error={errors.linkedin_url?.message}
            >
              <Input
                type="url"
                {...register("linkedin_url")}
                className="h-10 rounded-lg border-slate-200"
                placeholder="https://linkedin.com/in/…"
              />
            </Field>

            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2.5">
              <div>
                <div className="text-sm font-medium text-slate-900">
                  Decision maker
                </div>
                <p className="text-xs text-slate-500">
                  Mark as a key contact for sales
                </p>
              </div>
              <Switch
                checked={isDM}
                onCheckedChange={(v) => setValue("is_decision_maker", v, { shouldDirty: true })}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !isDirty}
              variant="primary"
              >
                {isSubmitting && (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
}

function emptyToNull(v: string | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  return v.trim() === "" ? null : v.trim();
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Save failed.";
}
